import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Link,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  CheckCircleOutlined,
  WarningAmber,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import CustomButton from "../../components/common/CustomButton";
import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { authApi } from "../../api/authApi";
import { passwordRule } from "../../components/validator/commonRules";
import { useAuth } from "../../contexts/AuthContext";

// ─── Constants ───────────────────────────────────────────────────────────────
const OTP_TTL = 600; // 10 minutes in seconds
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Password strength helper ─────────────────────────────────────────────────
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: "", color: "transparent" };
  let score = 0;
  if (password.length >= 13) score++;         // min length met
  if (/[A-Z]/.test(password)) score++;        // uppercase
  if (/[a-z]/.test(password)) score++;        // lowercase
  if (/\d/.test(password)) score++;           // number
  if (/[^A-Za-z0-9]/.test(password)) score++; // special character

  const levels = [
    { label: "Too weak", color: "#ef4444" },
    { label: "Weak", color: "#f97316" },
    { label: "Fair", color: "#eab308" },
    { label: "Good", color: "#22c55e" },
    { label: "Strong", color: "#16a34a" },
    { label: "Very strong", color: "#15803d" },
  ];
  return { score, ...levels[score] };
};

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepIndicator = ({ current }) => {
  const steps = ["Email", "OTP", "Password"];
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3, gap: 1 }}>
      {steps.map((label, idx) => {
        const step = idx + 1;
        const done = current > step;
        const active = current === step;
        return (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 700,
                bgcolor: done ? "#22c55e" : active ? "primary.main" : "grey.300",
                color: done || active ? "#fff" : "grey.600",
                transition: "all 0.3s",
              }}
            >
              {done ? <CheckCircleOutlined sx={{ fontSize: 16 }} /> : step}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: active ? 700 : 400,
                color: active ? "primary.main" : done ? "#22c55e" : "text.secondary",
                display: { xs: "none", sm: "block" },
              }}
            >
              {label}
            </Typography>
            {idx < steps.length - 1 && (
              <Box
                sx={{
                  width: 32,
                  height: 2,
                  bgcolor: done ? "#22c55e" : "grey.300",
                  mx: 0.5,
                  transition: "all 0.3s",
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ForgotPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { otp_end_time, forget_pwd_email, setAuthData } = useAuth();

  // ── Shared state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // ── Step 1 state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");

  // ── Step 2 state ──────────────────────────────────────────────────────────
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(OTP_TTL);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef(null);

  // ── Step 3 state ──────────────────────────────────────────────────────────
  const [tempToken, setTempToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({ newPassword: "", confirmPassword: "" });

  // ── Helpers ──────────────────────────────────────────────────────────────
  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // Start / restart OTP countdown
  const startOtpTimer = (initialTime = OTP_TTL) => {
    clearInterval(timerRef.current);
    const endTime = Date.now() + initialTime * 1000;
    setAuthData({ otp_end_time: endTime.toString() });

    setOtpTimer(initialTime);
    setCanResend(false);
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setOtpTimer(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setCanResend(true);
      }
    }, 1000);
  };

  useEffect(() => {
    const savedEndTime = otp_end_time;
    const savedEmail = forget_pwd_email;
    if (savedEndTime && savedEmail) {
      setEmail(savedEmail);
      const remaining = Math.max(0, Math.floor((parseInt(savedEndTime) - Date.now()) / 1000));
      if (remaining > 0) {
        // OTP still valid — resume countdown and jump to step 2
        setStep(2);
        startOtpTimer(remaining);
      } else {
        // OTP already expired — show expired state on step 2
        setStep(2);
        setOtpTimer(0);
        setCanResend(true);
        setAuthData({ otp_end_time: null });
      }
    }
    return () => clearInterval(timerRef.current);
  }, []);

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);

    if (!normalizedEmail) return showSnackbar("Email address is required.", "warning");
    if (!EMAIL_REGEX.test(normalizedEmail)) return showSnackbar("Please enter a valid email address.", "warning");

    setLoading(true);
    try {
      const response = await authApi.forgotPassword({ email: normalizedEmail });
      const { status, message } = response || {};

      if (status === 0) return showSnackbar("Network error. Please check your connection.", "warning");
      if (status >= 500) return showSnackbar("Server error. Please try again later.", "error");
      if (status === 404 || status === 400 || status === 401) {
        return showSnackbar(message || "Email not registered with any account.", "error");
      }
      if (status === 200) {
        showSnackbar("OTP sent to your email address!", "success");
        setAuthData({ forget_pwd_email: email.trim().toLowerCase() });
        startOtpTimer();
        setStep(2);
      }
    } catch {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);
    const normalizedOtp = otp.trim();
    setOtp(normalizedOtp);

    if (otpTimer <= 0) return showSnackbar("OTP has expired. Please request a new one.", "error");
    if (!normalizedOtp || normalizedOtp.length !== 6) return showSnackbar("Please enter the 6-digit OTP.", "warning");

    setLoading(true);
    try {
      const response = await authApi.verifyOtp({ email: normalizedEmail, otp: normalizedOtp });
      const { status, message, data } = response || {};

      if (status === 0) return showSnackbar("Network error. Please check your connection.", "warning");
      if (status >= 500) return showSnackbar("Server error. Please try again later.", "error");
      if (status === 401 || status === 400) {
        return showSnackbar(message || "Invalid or expired OTP.", "error");
      }
      if (status === 200) {
        clearInterval(timerRef.current);
        setAuthData({ otp_end_time: null });
        setTempToken(data?.temp_token || "");
        showSnackbar("OTP verified! Please set your new password.", "success");
        setStep(3);
      }
    } catch {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Resend OTP ────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (!canResend) return;
    setOtp("");
    await handleSendOtp();
  };

  // ── Step 3: Reset Password ────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();

    const trimmedPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    setNewPassword(trimmedPassword);
    setConfirmPassword(trimmedConfirmPassword);

    // Validate new password using shared passwordRule
    try {
      passwordRule("Password", { required: true }).validateSync(trimmedPassword);
    } catch (err) {
      setPasswordErrors((prev) => ({ ...prev, newPassword: err.message }));
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setPasswordErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);

    setLoading(true);
    try {
      const response = await authApi.resetPassword({
        email: normalizedEmail,
        temp_token: tempToken,
        new_password: trimmedPassword,
      });
      const { status, message } = response || {};

      if (status === 0) return showSnackbar("Network error. Please check your connection.", "warning");
      if (status >= 500) return showSnackbar("Server error. Please try again later.", "error");
      if (status === 401 || status === 400) {
        return showSnackbar(message || "Session expired. Please restart the process.", "error");
      }
      if (status === 200) {
        showSnackbar("Password updated successfully! Redirecting to login...", "success");
        setTimeout(() => navigate(`/login?email=${encodeURIComponent(normalizedEmail)}`), 2000);
      }
    } catch {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(newPassword);

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.background.grad ||
          "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)",
        p: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <CustomCard
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(14,165,233,0.18)",
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={1}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 0.5 }}
            >
              {step === 1 && "🔐 Forgot Password"}
              {step === 2 && "📩 Verify OTP"}
              {step === 3 && "🔑 New Password"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }}>
              {step === 1 && "Enter your registered email to receive an OTP"}
              {step === 2 && `OTP sent to ${email}`}
              {step === 3 && "Create a strong new password"}
            </Typography>
          </Box>

          {/* Step indicator */}
          <StepIndicator current={step} />

          {/* ── Step 1: Email ── */}
          {step === 1 && (
            <form onSubmit={handleSendOtp}>
              <CustomInput
                label="Email Address"
                name="email"
                type="email"
                required
                size="small"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <CustomButton
                type="submit"
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Send OTP"}
              </CustomButton>

              <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "text.secondary", fontSize: "0.8rem" }}>
                Remembered your password?{" "}
                <Link
                  onClick={() => navigate("/email/redirect")}
                  underline="hover"
                  sx={{ color: theme.palette.primary.main, fontWeight: 600, cursor: "pointer" }}
                >
                  Login
                </Link>
              </Typography>
            </form>
          )}

          {/* ── Step 2: OTP ── */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp}>
              <CustomInput
                label="Digit OTP"
                name="otp"
                // type="number"
                required
                size="small"
                value={otp}
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: {
                    textAlign: "center",
                    fontSize: "1.2rem",
                    fontWeight: 900,
                    letterSpacing: 2,
                    //   fontFamily: "inherit",
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setOtp(val);
                }}
              />

              {/* Countdown — hidden once OTP expires (banner below replaces it) */}
              {otpTimer > 0 && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, mb: 1 }}>
                    <Typography variant="caption" color={otpTimer < 60 ? "error" : "text.secondary"}>
                      {`Expires in ${formatTimer(otpTimer)}`}
                    </Typography>
                    <Link
                      onClick={handleResendOtp}
                      underline="hover"
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        cursor: canResend ? "pointer" : "default",
                        color: canResend ? theme.palette.primary.main : "text.disabled",
                        pointerEvents: canResend ? "auto" : "none",
                      }}
                    >
                      Resend OTP
                    </Link>
                  </Box>

                  {/* OTP progress bar */}
                  <LinearProgress
                    variant="determinate"
                    value={(otpTimer / OTP_TTL) * 100}
                    sx={{
                      mb: 2,
                      height: 3,
                      borderRadius: 2,
                      bgcolor: "grey.200",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: otpTimer < 60 ? "#ef4444" : theme.palette.primary.main,
                      },
                    }}
                  />
                </>
              )}

              {otpTimer <= 0 && (
                <Box
                  sx={{
                    mt: 0.5,
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(239,68,68,0.07)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
                    <WarningAmber sx={{ color: "#ef4444", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 700 }}>
                      Your OTP has expired.
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    Request a new OTP to continue resetting your password.
                  </Typography>
                  <CustomButton
                    fullWidth
                    disabled={loading}
                    onClick={handleResendOtp}
                    sx={{ height: 40, borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Resend OTP"}
                  </CustomButton>
                </Box>
              )}

              {otpTimer > 0 && (
                <CustomButton
                  type="submit"
                  fullWidth
                  disabled={loading || otp.length !== 6}
                // sx={{ height: 44, borderRadius: 2.5, fontWeight: 700, textTransform: "none" }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Verify OTP"}
                </CustomButton>
              )}

              <Typography
                variant="body2"
                onClick={() => { setStep(1); setOtp(""); clearInterval(timerRef.current); setAuthData({ otp_end_time: null }); }}
                sx={{ mt: 2, textAlign: "center", fontSize: "0.8rem", color: theme.palette.primary.main, fontWeight: 600, cursor: "pointer" }}
              >
                ← Change Email
              </Typography>
            </form>
          )}

          {/* ── Step 3: New Password ── */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <CustomInput
                label="New Password"
                name="newPassword"
                required
                size="small"
                tooltip="Password must be at least 13 characters long and contain at least one uppercase letter, one lowercase letter, one special character and one number."
                type={showNew ? "text" : "password"}
                value={newPassword}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
                onChange={(e) => {
                  const val = e.target.value?.trim();
                  setNewPassword(val);
                  try {
                    passwordRule("Password", { required: true }).validateSync(val);
                    setPasswordErrors((prev) => ({ ...prev, newPassword: "" }));
                  } catch (err) {
                    setPasswordErrors((prev) => ({ ...prev, newPassword: err.message }));
                  }
                  // Re-validate confirm match if already typed
                  if (confirmPassword) {
                    setPasswordErrors((prev) => ({
                      ...prev,
                      confirmPassword: val === confirmPassword.trim() ? "" : "Passwords do not match.",
                    }));
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew((p) => !p)} edge="end" size="small">
                        {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              {/* Password strength bar */}
              {newPassword && (
                <Box sx={{ mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(strength.score / 5) * 100}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: "grey.200",
                      "& .MuiLinearProgress-bar": { bgcolor: strength.color },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600, fontSize: "0.72rem" }}>
                    {strength.label}
                  </Typography>
                </Box>
              )}

              <CustomInput
                label="Confirm Password"
                name="confirmPassword"
                required
                size="small"
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword || " "}
                sx={{ mb: 1 }}
                onChange={(e) => {
                  const val = e.target.value?.trim();
                  setConfirmPassword(val);
                  setPasswordErrors((prev) => ({
                    ...prev,
                    confirmPassword: newPassword.trim() === val ? "" : "Passwords do not match.",
                  }));
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small">
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Match indicator
              {confirmPassword && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: newPassword === confirmPassword ? "#22c55e" : "#ef4444",
                    mb: 1,
                    display: "block",
                  }}
                >
                  {newPassword === confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
                </Typography>
              )} */}

              <CustomButton
                type="submit"
                fullWidth
                disabled={loading || passwordErrors.newPassword || passwordErrors.confirmPassword}
              // sx={{ mt: 1.5, height: 44, borderRadius: 2.5, fontWeight: 700, textTransform: "none" }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Update Password"}
              </CustomButton>
            </form>
          )}
        </CustomCard>
      </Box>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        autoHideDuration={6000}
      />
    </Box>
  );
};

export default ForgotPassword;
